import {
  Alert,
  Box,
  Button,
  Link as MuiLink,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { useAuth } from '../../auth/AuthProvider';
import type { DataSource } from '../../config/capabilities';
import { useRepository } from '../../data/RepositoryProvider';
import { useResource } from '../../hooks/useResource';
import { paths } from '../../routes/paths';
import { PageState } from '../../shared/data/PageState';
import { DetailHeader } from '../../shared/detail/DetailHeader';
import { MetadataList } from '../../shared/detail/MetadataList';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import { toPageError } from '../partners/PartnerListPage';
import { ReportDrilldowns } from '../reports/ReportDrilldowns';
import { walletsRepositoryToken } from './repository';
import type { WalletDetail } from './types';
import { formatWalletMoney } from './types';
import { WalletAdjustmentDialog } from './WalletAdjustmentDialog';
import { WalletLedgerPanel } from './WalletLedgerPanel';
import { WalletStatusChip } from './WalletListPage';
import { WalletStatusDialog } from './WalletStatusDialog';

export function WalletDetailPage({ dataSource }: { readonly dataSource: DataSource }) {
  const { walletId = '' } = useParams();
  const repository = useRepository(walletsRepositoryToken);
  const auth = useAuth();
  const { i18n, t } = useTranslation();
  const language: TimeLanguage = i18n.language.startsWith('th') ? 'th' : 'en';
  const [tab, setTab] = useState<'overview' | 'ledger' | 'related'>('overview');
  const [statusOpen, setStatusOpen] = useState(false);
  const [adjustmentOpen, setAdjustmentOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const resource = useResource(
    (signal) => repository.get(walletId, signal),
    [refreshKey, repository, walletId],
    () => false,
  );
  const permissions = new Set(
    auth.status === 'authenticated' ? auth.principal.permissions : ([] as readonly string[]),
  );
  const canManageStatus = permissions.has('WALLET_STATUS_MANAGE');
  const canAdjust = permissions.has('WALLET_ADJUST');
  const canViewApproval = permissions.has('APPROVAL_DECIDE');

  return (
    <Box sx={{ minWidth: 0 }}>
      <PageState
        error={toPageError(resource.error, t('wallets.errors.detail'))}
        onRetry={() => setRefreshKey((value) => value + 1)}
        status={resource.status}
      >
        {resource.data === null ? null : (
          <Stack spacing={2}>
            <DetailHeader
              actions={
                canAdjust || (canManageStatus && resource.data.status !== 'CLOSED') ? (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    {canAdjust ? (
                      <Button onClick={() => setAdjustmentOpen(true)} variant="contained">
                        {t('wallets.actions.createAdjustment')}
                      </Button>
                    ) : null}
                    {canManageStatus && resource.data.status !== 'CLOSED' ? (
                      <Button
                        color={resource.data.status === 'ACTIVE' ? 'warning' : 'success'}
                        onClick={() => setStatusOpen(true)}
                        variant="outlined"
                      >
                        {resource.data.status === 'ACTIVE'
                          ? t('wallets.actions.suspendWallet')
                          : t('wallets.actions.reactivateWallet')}
                      </Button>
                    ) : null}
                  </Stack>
                ) : undefined
              }
              backTo={paths.affiliateWallets}
              dataSource={dataSource}
              subtitle={`${resource.data.partner.code} · ${resource.data.partnerUserId ?? resource.data.memberId}`}
              title={resource.data.username ?? resource.data.id}
            />
            {resource.data.reconciliation.status === 'MISMATCHED' ? (
              <Alert severity="warning">{t('wallets.reconciliationWarning')}</Alert>
            ) : null}
            <Tabs
              aria-label={t('wallets.detail.tabsLabel')}
              onChange={(_event, value) => setTab(value as typeof tab)}
              value={tab}
              variant="scrollable"
            >
              <Tab label={t('wallets.detail.overview')} value="overview" />
              <Tab label={t('wallets.detail.ledger')} value="ledger" />
              <Tab label={t('wallets.detail.related')} value="related" />
            </Tabs>
            {tab === 'overview' ? (
              <WalletOverview language={language} wallet={resource.data} />
            ) : null}
            {tab === 'ledger' ? (
              <WalletLedgerPanel
                language={language}
                totalEntryCount={resource.data.entryCount}
                walletId={resource.data.id}
              />
            ) : null}
            {tab === 'related' ? (
              <RelatedRecords permissions={permissions} wallet={resource.data} />
            ) : null}
            <ReportDrilldowns
              metrics={[
                {
                  kind: 'wallets',
                  filters: {
                    memberId: resource.data.memberId,
                    currency: resource.data.currencyCode,
                  },
                  label: t('reportSummaries.metrics.walletReconciliation'),
                  value: `${resource.data.reconciliation.status} · ${
                    formatTimestamp(
                      resource.data.reconciliation.checkedAt,
                      'Asia/Bangkok',
                      language,
                    ).display
                  }`,
                },
              ]}
            />
          </Stack>
        )}
      </PageState>
      {resource.data === null ? null : (
        <>
          <WalletStatusDialog
            onClose={() => setStatusOpen(false)}
            onSaved={() => {
              setStatusOpen(false);
              setRefreshKey((value) => value + 1);
            }}
            open={statusOpen}
            wallet={resource.data}
          />
          <WalletAdjustmentDialog
            canViewApproval={canViewApproval}
            dataSource={dataSource}
            onClose={() => setAdjustmentOpen(false)}
            open={adjustmentOpen}
            wallet={resource.data}
          />
        </>
      )}
    </Box>
  );
}

function WalletOverview({
  language,
  wallet,
}: {
  readonly language: TimeLanguage;
  readonly wallet: WalletDetail;
}) {
  const { t } = useTranslation();
  const lastEntry =
    wallet.lastEntryAt === null
      ? null
      : formatTimestamp(wallet.lastEntryAt, 'Asia/Bangkok', language);
  const reconciled = formatTimestamp(wallet.reconciliation.checkedAt, 'Asia/Bangkok', language);
  const created = formatTimestamp(wallet.createdAt, 'Asia/Bangkok', language);
  const updated = formatTimestamp(wallet.updatedAt, 'Asia/Bangkok', language);
  const money = (amount: string) =>
    formatWalletMoney(amount, wallet.currencyCode, wallet.currencyMinorUnit);
  return (
    <Stack spacing={2}>
      <Paper component="section" sx={{ p: 2 }} variant="outlined">
        <Typography component="h2" sx={{ mb: 2 }} variant="h5">
          {t('wallets.detail.balances')}
        </Typography>
        <MetadataList
          items={[
            { label: t('wallets.fields.available'), value: money(wallet.availableBalance) },
            { label: t('wallets.fields.reserved'), value: money(wallet.reservedBalance) },
            { label: t('wallets.fields.total'), value: money(wallet.totalBalance) },
            { label: t('wallets.fields.lifetimeEarned'), value: money(wallet.lifetimeEarned) },
            { label: t('wallets.fields.lifetimeReversed'), value: money(wallet.lifetimeReversed) },
            { label: t('wallets.fields.lifetimeAdjusted'), value: money(wallet.lifetimeAdjusted) },
            {
              label: t('wallets.fields.lifetimeWithdrawn'),
              value: money(wallet.lifetimeWithdrawn),
            },
          ]}
        />
      </Paper>
      <Paper component="section" sx={{ p: 2 }} variant="outlined">
        <Typography component="h2" sx={{ mb: 2 }} variant="h5">
          {t('wallets.detail.identity')}
        </Typography>
        <MetadataList
          items={[
            { label: t('wallets.fields.walletId'), value: wallet.id },
            {
              label: t('wallets.fields.member'),
              value: (
                <MuiLink component={Link} to={`/members/${wallet.memberId}`}>
                  {wallet.username ?? wallet.memberId}
                </MuiLink>
              ),
            },
            {
              label: t('wallets.fields.partner'),
              value: (
                <MuiLink component={Link} to={`/partners/${wallet.partner.id}`}>
                  {wallet.partner.code} · {wallet.partner.name}
                </MuiLink>
              ),
            },
            {
              label: t('wallets.fields.agent'),
              value:
                wallet.agent === null ? (
                  t('common.notAvailable')
                ) : (
                  <MuiLink component={Link} to={`/agents/${wallet.agent.id}`}>
                    {wallet.agent.code} · {wallet.agent.name}
                  </MuiLink>
                ),
            },
            {
              label: t('wallets.fields.partnerUserId'),
              value: wallet.partnerUserId ?? t('common.notAvailable'),
            },
            {
              label: t('wallets.fields.status'),
              value: <WalletStatusChip status={wallet.status} />,
            },
            {
              label: t('wallets.fields.currency'),
              value: `${wallet.currencyCode} (${wallet.currencyMinorUnit})`,
            },
            { label: t('wallets.fields.version'), value: wallet.version },
            { label: t('wallets.fields.entries'), value: wallet.entryCount },
            { label: t('wallets.fields.withdrawals'), value: wallet.withdrawalCount },
            {
              label: t('wallets.fields.reconciliation'),
              value: `${wallet.reconciliation.status} · ${reconciled.display} (${reconciled.originalOffset})`,
            },
            {
              label: t('wallets.fields.lastEntryAt'),
              value:
                lastEntry === null
                  ? t('common.notAvailable')
                  : `${lastEntry.display} (${lastEntry.originalOffset})`,
            },
            {
              label: t('wallets.fields.createdAt'),
              value: `${created.display} (${created.originalOffset})`,
            },
            {
              label: t('wallets.fields.updatedAt'),
              value: `${updated.display} (${updated.originalOffset})`,
            },
          ]}
        />
      </Paper>
    </Stack>
  );
}

function RelatedRecords({
  permissions,
  wallet,
}: {
  readonly permissions: ReadonlySet<string>;
  readonly wallet: WalletDetail;
}) {
  const { t } = useTranslation();
  return (
    <Paper component="section" sx={{ p: 2 }} variant="outlined">
      <Typography component="h2" sx={{ mb: 2 }} variant="h5">
        {t('wallets.detail.related')}
      </Typography>
      <Stack spacing={1}>
        {permissions.has('WITHDRAWAL_VIEW') ? (
          <MuiLink component={Link} to={`/affiliate-withdrawals?walletId=${wallet.id}`}>
            {t('wallets.links.withdrawals')} · {wallet.withdrawalCount}
          </MuiLink>
        ) : null}
        {permissions.has('AUDIT_VIEW') ? (
          <MuiLink component={Link} to={`/audit?entityType=AFFILIATE_WALLET&entityId=${wallet.id}`}>
            {t('wallets.links.audit')}
          </MuiLink>
        ) : null}
      </Stack>
    </Paper>
  );
}
